<template>
    <MigratedComplianceSection
        class="JsonSchemaMigratedComplianceForm"
        :class="$attrs.class"
        :count="migratedRequirements.length"
        :has-error="hasError"
    >
        <JsonSchemaObjectForm
            v-bind="passThroughAttrs"
            :form="form"
            :value="value"
        />
    </MigratedComplianceSection>
</template>

<style lang="scss">
    // stylelint-disable selector-max-combinators, selector-max-compound-selectors, selector-max-class
    .JsonSchemaMigratedComplianceForm {
        .JsonSchemaObjectForm {
            margin: 0;
        }

        .JsonSchemaObjectForm__content {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        // Each date reads as "requirement name / Valid until" beside its input, with any
        // error under the input.
        .FormElement {
            align-items: center;
            column-gap: 12px;
            display: grid;
            grid-template-columns: minmax(0, 1fr) 170px;
            margin: 0;
        }

        // The form reserves a line under every input for an error message; here it takes
        // space only when there is an error.
        .FormInput__errorMessage {
            margin-top: 0;
            padding: 0;
        }

        .FormElement__label {
            grid-column: 1;
            grid-row: 1;

            &::after {
                color: var(--eidTechGray);
                content: "Valid until";
                display: block;
                font-size: 12px;
            }
        }

        .FormElement__input {
            grid-column: 2;
            grid-row: 1;
        }

        .FormElement__errorMessage {
            grid-column: 2;
            grid-row: 2;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import omit from 'lodash/omit';

    import JsonSchemaObjectForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/elements/JsonSchemaObjectForm.vue';
    import { JsonFormObject } from '@evidentid/json-schema/interfaces/JsonForm';

    import {
        getMigratedRequirements,
        MigratedRequirement,
    } from '@/components/migrated-compliance-section/migratedCompliance';
    import MigratedComplianceSection from '@/components/migrated-compliance-section/MigratedComplianceSection.vue';

    // Renders the `exceptions` group of the Add Entity form as the "Migrate existing compliance"
    // section. It is the stock object form inside the section, so values, validation and touch
    // handling are the form's own; every other prop and listener is passed straight through.
    export default defineComponent({
        name: 'JsonSchemaMigratedComplianceForm',
        components: { JsonSchemaObjectForm, MigratedComplianceSection },
        inheritAttrs: false,
        props: {
            form: {
                type: Object as PropType<JsonFormObject>,
                required: true,
            },
            value: {
                type: Object as PropType<Record<string, string | null | undefined> | null>,
                default: null,
            },
        },
        computed: {
            passThroughAttrs(): Record<string, unknown> {
                return omit(this.$attrs, [ 'class', 'style' ]);
            },
            requirementTypes(): string[] {
                return this.form.getProperties(this.value).map((property) => property.name);
            },
            migratedRequirements(): MigratedRequirement[] {
                return getMigratedRequirements(this.value, this.requirementTypes);
            },
            hasError(): boolean {
                return !this.form.isValid(this.form.getValue(this.value), true);
            },
        },
    });
</script>
